import React, {useEffect, useState} from "react";
import {
  Button, Checkbox,
  Fade,
  IconButton,
  Stack,
  Table,
  TableBody, TableCell,
  TableHead,
  TableRow,
  TextField
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {IconPencil, IconPlus, IconTrash, IconX} from "@tabler/icons-react";
import Modal from "@mui/material/Modal";
import {grey} from "@mui/material/colors";
import {v4 as uuid} from "uuid"
import {CreateToolParameter} from "./CreateToolParameter";
import {ParserModal} from "../../../../../components/modals/ParserModal";
import {Editor} from "@monaco-editor/react";
import {HookSettings} from "../../edit-menu/settings/HookSettings";

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: "320px",
  maxWidth: "md", width: "90%",
  maxHeight: "80vh",
  bgcolor: 'background.paper',
  borderRadius: 0,
  border: '0px solid #000',
  boxShadow: 24,
  p: 3,
  overflowY: "auto"
};

const initState = {
  type: "function",
  function: {
    name: "",
    description: "",
    parameters: {
      type: "object",
      properties: {},
      required: [],
      additionalProperties: false
    },
    strict: true
  },
  javascript: `
function (args) {
  return ""
}`
}


export const CreateToolModal = ({open, handleClose, selectedTool, handleSubmit, project}) => {
  const [createParam, setCreateParam] = useState(false);
  const [editParam, setEditParam] = useState(null);
  const [expandPersona, setExpandPersona] = useState(false);
  const [expandBodyParams, setExpandBodyParams] = useState(false);
  const [tool, setTool] = useState({...initState})

  useEffect(() => {
    if (selectedTool && project) {
      setTool({...selectedTool})
    }
  }, [project, selectedTool]);

  const onCreateConst = async () => {
    if (!tool?.function?.name) {
      return;
    }
    const data = {...project};
    const newCollection = selectedTool ? {...selectedTool, ...tool} : {...tool, id: uuid()};
    if (!data?.tools) {
      data.tools = [];
    }
    if (selectedTool) {
      data.tools = data?.tools?.map(i => i.id === newCollection.id ? {...newCollection} : {...i});
    } else {
      data.tools.push(newCollection);
    }

    handleSubmit(data);
    handleClose();
  }

  const onDeleteParam = (param) => {
    setTool(prev => {
      const data = {...prev}
      delete data.function.parameters.properties[param];
      data.function.parameters.required = data?.function?.parameters?.required?.filter(i => i !== param) || [];
      return data;
    })
  }

  const onDelete = async () => {
    if (!selectedTool) {
      return;
    }
    const data = {...project};
    data.tools = data?.tools?.filter(i => i?.id !== selectedTool?.id) || []

    handleSubmit(data);
    handleClose();
  }

  const onClose = () => {
    setTool({...initState})
    handleClose();
  }

  const onChangeData = (key, value) => {
    setTool(prev => {
      const state = {...prev};
      state.data[key] = value;
      return state;
    })
  }

  return (
    <>
      <ParserModal
        handleClose={() => setExpandPersona(false)}
        open={expandPersona}
        onSuccess={(content) => setTool(prev => ({...prev, javascript: content}))}
        text={tool?.javascript || ""}
      />
      <CreateToolParameter
        tool={tool} setTool={setTool}
        open={createParam} selectedParameter={editParam}
        handleClose={() => {
          setEditParam(null);
          setCreateParam(false);
        }}
      />
      <ParserModal
        handleClose={() => setExpandBodyParams(false)}
        open={expandBodyParams}
        onSuccess={(content) => setTool(prev => ({...prev, body: content}))}
        text={tool?.body || ""}
      />
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Fade in={open} timeout={500}>
          <Box>
            <Box sx={{position: "fixed", top: 10, right: 10}}>
              <IconButton
                size={"small"}
                onClick={onClose}
                color={"#FFF"}
              >
                <IconX color={grey[300]}/>
              </IconButton>
            </Box>

            <Stack direction={"column"} gap={2} sx={style}>
              <Typography variant={"h4"} fontWeight={"bold"}
                          sx={{mb: 1}}>{selectedTool ? "Update" : "Create"} Tool</Typography>
              <Stack direction={"column"} gap={2} sx={{flex: 1, overflowY: "auto"}}>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Name:</Typography>
                  <TextField
                    size={"small"} placeholder={"Enter name of the tool"}
                    helperText={"Name should be unique"}
                    value={tool?.function?.name} autoFocus fullWidth
                    onChange={e => setTool(prev => ({
                      ...prev,
                      function: {
                        ...prev.function,
                        name: e?.target?.value?.replaceAll(" ", "_")?.toLowerCase()
                      },
                      javascript: prev?.javascript?.replaceAll(`function ${prev?.function?.name}(args) {`, `function ${e?.target?.value?.replaceAll(" ", "_")?.toLowerCase()}(args) {`)
                    }))}
                  />
                </Box>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Description:</Typography>
                  <TextField
                    size={"small"} placeholder={"Describe tool"}
                    multiline rows={3}
                    value={tool?.function?.description} fullWidth
                    onChange={e => setTool(prev => ({
                      ...prev,
                      function: {
                        ...prev.function,
                        description: e?.target?.value
                      }
                    }))}
                  />
                </Box>
                <Box sx={{maxHeight: "30dvh", minHeight: "30dvh", height: "100dvh", overflowY: "auto"}}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{fontWeight: 600}}>Name</TableCell>
                        <TableCell sx={{fontWeight: 600}}>Type</TableCell>
                        <TableCell sx={{fontWeight: 600}}>Description</TableCell>
                        <TableCell sx={{fontWeight: 600}}>Required</TableCell>
                        <TableCell sx={{fontWeight: 600}} padding={"checkbox"}>
                          <IconButton onClick={() => setCreateParam(true)} size={"small"}><IconPlus
                            size={17}/></IconButton>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(tool.function.parameters?.properties)?.length === 0 ?
                        <TableRow><TableCell colSpan={5}>Please add some parameters</TableCell></TableRow>
                        : null
                      }
                      {
                        Object.keys(tool.function.parameters?.properties)
                          ?.map(key => {
                            const param = tool.function.parameters.properties[key] || {};
                            const isRequired = tool?.function?.parameters?.required?.includes(key);
                            return (
                              <TableRow key={key} hover>
                                <TableCell>{key}</TableCell>
                                <TableCell>{param?.type}</TableCell>
                                <TableCell><Typography noWrap>{param?.description}</Typography></TableCell>
                                <TableCell padding={"checkbox"}>
                                  <Checkbox
                                    size={"small"}
                                    checked={isRequired}
                                    onChange={(e, checked) => {
                                      setTool(prev => ({
                                        ...prev,
                                        function: {
                                          ...prev.function,
                                          parameters: {
                                            ...prev.function.parameters,
                                            required: checked ? [...prev.function.parameters.required, key] : prev.function.parameters.required?.filter(i => i !== key)
                                          }
                                        }
                                      }))
                                    }}
                                  />
                                </TableCell>
                                <TableCell padding={"checkbox"}>
                                  <Stack direction={"row"} gap={1}>
                                    <IconButton
                                      size={"small"}
                                      onClick={() => {
                                        setCreateParam(true);
                                        setEditParam(key)
                                      }}
                                    >
                                      <IconPencil size={17}/>
                                    </IconButton>
                                    <IconButton onClick={() => onDeleteParam(key)} size={"small"}><IconTrash size={17}/></IconButton>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            )
                          })
                      }
                    </TableBody>
                  </Table>
                </Box>
                <HookSettings
                  node={tool} setNode={setTool} onUpdate={() => {}} onChangeData={onChangeData}
                />
                <Box onClick={() => setExpandPersona(true)}>
                  <Typography fontWeight={600} gutterBottom>Function code:</Typography>
                  <Editor
                    height="200px"
                    defaultLanguage="javascript"
                    value={tool?.javascript || ""}
                    // onChange={(value, e) => onChangeData(isHook ? "api_output_parser" : "parser", value)}
                  />
                </Box>

              </Stack>

              <Stack direction={'row'} gap={1} useFlexGap flexWrap={"wrap"}>
                <Button
                  variant={"contained"}
                  color={"secondary"}
                  sx={{borderRadius: 0, textTransform: "capitalize"}}
                  disabled={!tool.function?.name}
                  onClick={onCreateConst}
                >
                  Save
                </Button>
                <Button
                  variant={"outlined"}
                  color={"secondary"}
                  sx={{borderRadius: 0, textTransform: "capitalize"}}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                {
                  selectedTool && <Button
                    variant={"outlined"}
                    color={"error"}
                    sx={{borderRadius: 0, textTransform: "capitalize", ml: "auto"}}
                    onClick={onDelete}
                  >
                    Delete
                  </Button>
                }
              </Stack>
            </Stack>
          </Box>
        </Fade>
      </Modal>
    </>
  )
}
